import React, { useImperativeHandle, useRef } from 'react';
import ReactSelect, { Props as RSelectProps, Theme as RSTheme } from 'react-select';
import CreatableSelect, { CreatableProps } from 'react-select/creatable';
import AsyncSelect, { AsyncProps } from 'react-select/async';
import AsyncCreatableSelect, { AsyncCreatableProps } from 'react-select/async-creatable';
import { DropdownIndicator } from './components/DropdownIndicator';
import { Option as OptionComponent } from './components/Option';
import { NoOptionsMessage } from './components/NoOptionsMessage';
import { GroupHeading } from './components/GroupHeading';
import { ClearIndicator } from './components/ClearIndicator';
import { LoadingMessage } from './components/LoadingMessage';
import { useTheme } from '../../../../theme/theme';
import { customTheme, defaultStyles } from './BaseSelect.styles';
import { GroupType } from './types';

export interface BaseSelectProps<Option, IsMulti extends boolean>
  extends RSelectProps<Option, IsMulti, GroupType<Option>>,
    CreatableProps<Option, IsMulti, GroupType<Option>>,
    AsyncCreatableProps<Option, IsMulti, GroupType<Option>>,
    AsyncProps<Option, IsMulti, GroupType<Option>> {
  isCreatable?: boolean;
  isAsync?: boolean;
  noWrap?: boolean;
}

export function BaseSelect<Option, IsMulti extends boolean = false>({
  variant = 'simple',
  onChange,
  innerRef,
  isPortal,
  isCreatable,
  isAsync,
  noOptionsMessage,
  noWrap,
  ...rest
}: BaseSelectProps<Option, IsMulti>) {
  const theme = useTheme();

  const inputRef = useRef<any>(null);

  useImperativeHandle(innerRef, () => ({
    focus: () => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    },
  }));

  const Component = isAsync
    ? isCreatable
      ? AsyncCreatableSelect
      : AsyncSelect
    : isCreatable
    ? CreatableSelect
    : ReactSelect;

  return (
    <Component
      {...rest}
      ref={inputRef}
      onChange={onChange}
      components={{
        DropdownIndicator,
        Option: OptionComponent,
        NoOptionsMessage,
        GroupHeading,
        ClearIndicator,
        LoadingMessage,
        ...rest.components,
      }}
      theme={(rsTheme: RSTheme) => customTheme(rsTheme, theme)}
      styles={{
        ...defaultStyles({ theme, formVariant: rest.formVariant, noWrap }),
        ...rest.styles,
      }}
      variant={variant}
      noOptionsMessage={noOptionsMessage || (() => 'No Available Options')}
      {...(isPortal
        ? { menuPortalTarget: document.body, menuShouldBlockScroll: true, menuPosition: 'fixed' }
        : {})}
    />
  );
}
